import React from 'react';
import { motion } from 'framer-motion';
import { X, Check, TrendingUp, BarChart3, GitBranch, Users, Handshake, Megaphone, ClipboardList, CalendarHeart } from 'lucide-react';
export function ProfessionalComparison() {
  const comparisons = [{
    situation: 'Publicás todos los días pero no crecen tus seguidores',
    without: 'Publicás sin estrategia de crecimiento',
    with: 'Sistema de crecimiento orgánico con acciones diarias probadas'
  }, {
    situation: 'Querés saber si tu contenido realmente genera ventas',
    without: 'Medís por sensación',
    with: 'Dashboard automático con métricas de conversión reales'
  }, {
    situation: 'Alguien te pregunta y no compra, y no sabés por qué',
    without: 'Perdés la venta sin entender qué falló',
    with: 'Funnel de ventas mapeado con punto de quiebre identificado'
  }, {
    situation: 'Necesitás saber a quién hacer seguimiento y cuándo',
    without: 'Lista mental que se olvida',
    with: 'CRM básico con estados, fechas y próximos pasos'
  }, {
    situation: 'Querés llegar a más gente sin pagar publicidad',
    without: 'Dependés solo del algoritmo',
    with: 'Estrategia de colaboraciones que multiplica el alcance orgánico'
  }, {
    situation: 'Querés empezar con publicidad pero no sabés cómo',
    without: 'Miedo a perder dinero',
    with: 'Guía paso a paso de Meta Ads para presupuestos pequeños'
  }];
  const modules = [{
    icon: <TrendingUp className="w-6 h-6" data-id="element-246" />,
    title: 'Tomo Pro 1 — Estrategia de Ventas Avanzada',
    color: 'text-violet-300',
    bgColor: 'bg-violet-500/20',
    items: 'Funnel de ventas para emprendedoras · Mapeo del customer journey · Cómo identificar el punto de quiebre · Sistema de conversión de consultas en ventas · Métricas que importan y cómo medirlas'
  }, {
    icon: <BarChart3 className="w-6 h-6" data-id="element-247" />,
    title: 'Tomo Pro 2 — Crecimiento Orgánico Acelerado',
    color: 'text-purple-300',
    bgColor: 'bg-purple-500/20',
    items: 'Algoritmo de Instagram explicado sin tecnicismos · Estrategia de hashtags por nivel · Acciones diarias de crecimiento (menos de 20 minutos) · Colaboraciones con otras cuentas · Contenido de alto alcance vs alta conversión'
  }, {
    icon: <Handshake className="w-6 h-6" data-id="element-248" />,
    title: 'Tomo Pro 3 — Colaboraciones y Alianzas',
    color: 'text-fuchsia-300',
    bgColor: 'bg-fuchsia-500/20',
    items: 'Cómo encontrar emprendedoras complementarias · Cómo proponer una colaboración · 5 formatos de colaboración con ejemplos reales · Medir el resultado de una colaboración · Construir una red de aliadas que impulse tu negocio'
  }, {
    icon: <GitBranch className="w-6 h-6" data-id="element-249" />,
    title: 'Tracker Pro en Excel',
    color: 'text-emerald-300',
    bgColor: 'bg-emerald-500/20',
    items: 'Dashboard automático de ventas semanales y mensuales · CRM básico con estado de cada cliente · Seguimiento de contenido con métricas de engagement · Calculadora de conversión de consultas a ventas'
  }, {
    icon: <Megaphone className="w-6 h-6" data-id="element-250" />,
    title: 'Bonus Pro 1 — Meta Ads para Emprendedoras',
    color: 'text-indigo-300',
    bgColor: 'bg-indigo-500/20',
    items: 'Cómo crear tu primera campaña en Facebook/Instagram Ads · Presupuesto mínimo viable · Segmentación para emprendedoras argentinas · Qué funciona y qué quema dinero · Errores más comunes de la emprendedora que empieza a pautar'
  }, {
    icon: <ClipboardList className="w-6 h-6" data-id="element-251" />,
    title: 'Bonus Pro 2 — CRM Básico',
    color: 'text-violet-300',
    bgColor: 'bg-violet-600/20',
    items: 'Plantilla completa para gestionar clientes · Estados de la venta claramente definidos · Sistema de seguimiento con fechas y próximos pasos · Registro de historial de compras por cliente · Alertas de reactivación de clientes inactivos'
  }, {
    icon: <CalendarHeart className="w-6 h-6" data-id="element-252" />,
    title: 'Bonus Pro 3 — Fechas Argentinas',
    color: 'text-purple-300',
    bgColor: 'bg-purple-600/20',
    items: 'Calendario completo de fechas especiales en Argentina · Ideas de contenido específicas para cada fecha · Copies de venta para cada ocasión · Anticipación recomendada para cada campaña · Fechas patrias, días del sector, fechas comerciales y estaciones'
  }, {
    icon: <Users className="w-6 h-6" data-id="element-253" />,
    title: 'Comunidad de Emprendedoras Pro',
    color: 'text-rose-300',
    bgColor: 'bg-rose-500/20',
    items: 'Grupo exclusivo de emprendedoras del Sistema Pro · Networking real con aliadas · Resolución de dudas con casos reales · Inspiración constante para no abandonar'
  }];
  return <section className="py-20 bg-black" data-id="element-254">
      <div className="container mx-auto px-4 max-w-5xl" data-id="element-255">
        <div className="text-center mb-12" data-id="element-256">
          <h2 className="md:text-4xl font-extrabold text-white mb-4 text-[20px]" data-id="element-257">
            🎯 ¿QUÉ AGREGA EL "SISTEMA PRO"?
          </h2>
          <p className="text-zinc-200 text-[14px]" data-id="element-258">
            La diferencia entre tener herramientas y tener el sistema más
            completo disponible.
          </p>
        </div>

        <div className="mb-16 overflow-x-auto pb-4" data-id="element-259">
          <div className="min-w-[800px]" data-id="element-260">
            <div className="grid grid-cols-3 gap-4 mb-4 px-4" data-id="element-261">
              <div className="font-bold text-violet-300 uppercase text-sm tracking-wider" data-id="element-262">
                Situación real
              </div>
              <div className="font-bold text-red-400 uppercase text-sm tracking-wider flex items-center gap-2" data-id="element-263">
                <X className="w-4 h-4" data-id="element-264" /> Sin Sistema Pro
              </div>
              <div className="font-bold text-emerald-400 uppercase text-sm tracking-wider flex items-center gap-2" data-id="element-265">
                <Check className="w-4 h-4" data-id="element-266" /> Con Sistema Pro
              </div>
            </div>

            <div className="space-y-3" data-id="element-267">
              {comparisons.map((row, i) => <div key={i} className="grid grid-cols-3 gap-4 bg-zinc-900/60 rounded-xl p-4 items-center border border-violet-900/30" data-id="element-268">
                  <div className="font-medium text-white" data-id="element-269">{row.situation}</div>
                  <div className="text-zinc-300 text-sm" data-id="element-270">{row.without}</div>
                  <div className="text-emerald-200 font-medium text-sm bg-emerald-500/15 p-2 rounded-lg border border-emerald-500/30" data-id="element-271">
                    {row.with}
                  </div>
                </div>)}
            </div>
          </div>
          <p className="text-center text-sm text-violet-400 mt-4 md:hidden" data-id="element-272">
            ← Scroll to see full table →
          </p>
        </div>

        <div className="bg-zinc-900 rounded-3xl p-8 md:p-12 text-white shadow-xl border border-violet-900/40" data-id="element-273">
          <h3 className="text-2xl font-bold mb-8 flex items-center gap-3 text-white" data-id="element-274">
            <span className="bg-emerald-500 text-white p-1 rounded-full" data-id="element-275">
              <Check className="w-5 h-5" data-id="element-276" />
            </span>
            EL SISTEMA PRO INCLUYE:
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-277">
            {modules.map((mod, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.08
          }} className="bg-black/50 rounded-2xl p-6 border border-violet-900/30" data-id="element-278">
                <div className="flex items-center gap-3 mb-4" data-id="element-279">
                  <div className={`p-2 rounded-lg ${mod.bgColor} ${mod.color}`} data-id="element-280">
                    {mod.icon}
                  </div>
                  <h4 className="font-bold text-lg text-white" data-id="element-281">{mod.title}</h4>
                </div>
                <p className="text-zinc-200 text-sm leading-relaxed" data-id="element-282">
                  {mod.items}
                </p>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}